/**
 * Campaign Manager V2 HTTP routes (Reward Center).
 *
 * Routes as redesigned on 2026-10-21 (API_ROUTES.md / FRONTEND_MIGRATION.md
 * in campaign-manager): `/<resource>/<operation>/<ids...>`, every fixed word
 * before the first id. The old `/campaigns/{id}/...` layout answers 404.
 *
 * Everything is campaign-first: a task is only ever addressed through its
 * campaign, and there is no endpoint that returns tasks across campaigns.
 *
 * Each personal read has a public twin for guests. A screen calls one or the
 * other, never both: the personal response already carries every public
 * field, and a personal route without a token answers 401.
 */
export const CAMPAIGN_MANAGER_BASE = '/api/v1/campaign-manager';

/* Personal: the caller's own state. Needs a login. */

/** Campaign list with the caller's own progress. */
export const CAMPAIGNS = `${CAMPAIGN_MANAGER_BASE}/campaigns/progress/list`;

/** The single active special campaign, or `{ campaign: null }`. */
export const SPECIAL_CAMPAIGN = `${CAMPAIGN_MANAGER_BASE}/campaigns/progress/special`;

export const CAMPAIGN_TASKS = `${CAMPAIGN_MANAGER_BASE}/campaigns/tasks/progress/list/{campaign_id}`;

/** Only callable when `task.selfCompletable` is true. */
export const CAMPAIGN_TASK_COMPLETE = `${CAMPAIGN_MANAGER_BASE}/campaigns/tasks/complete/{campaign_id}/{task_id}`;

/** Claims every reward on the task atomically. */
export const CAMPAIGN_TASK_CLAIM = `${CAMPAIGN_MANAGER_BASE}/campaigns/tasks/claim/{campaign_id}/{task_id}`;

/** One feed for challenge / reward / campaign history, switched by `type`. */
export const REWARD_HISTORY = `${CAMPAIGN_MANAGER_BASE}/campaigns/history`;

/** Lottery summary for a campaign cycle, with the caller's tickets and weight. */
export const CAMPAIGN_LOTTERY = `${CAMPAIGN_MANAGER_BASE}/campaigns/lottery/tickets/{campaign_id}`;

/* Public: no login; no personal fields in the response. */

export const PUBLIC_CAMPAIGNS = `${CAMPAIGN_MANAGER_BASE}/campaigns/list`;

/** Not the personal special campaign, despite the old path being this one. */
export const PUBLIC_SPECIAL_CAMPAIGN = `${CAMPAIGN_MANAGER_BASE}/campaigns/special`;

export const PUBLIC_CAMPAIGN_TASKS = `${CAMPAIGN_MANAGER_BASE}/campaigns/tasks/list/{campaign_id}`;

/** A cycle's lottery totals, draw and prizes, without tickets. */
export const PUBLIC_CAMPAIGN_LOTTERY = `${CAMPAIGN_MANAGER_BASE}/campaigns/lottery/summary/{campaign_id}`;

/** Completed draws, newest first. The same for guests and users. */
export const LOTTERIES = `${CAMPAIGN_MANAGER_BASE}/campaigns/lottery/list`;

/** Masked winners for a drawn cycle. The same for guests and users. */
export const CAMPAIGN_LOTTERY_WINNERS = `${CAMPAIGN_MANAGER_BASE}/campaigns/lottery/winners/{campaign_id}`;

/** Fills `{campaign_id}` / `{task_id}` style placeholders in the paths above. */
export const withPathParams = (
  path: string,
  params: Record<string, string>
): string =>
  Object.entries(params).reduce(
    (url, [key, value]) => url.replace(`{${key}}`, encodeURIComponent(value)),
    path
  );
